import { useMemo } from "react";
import type { FavoriteItem } from "@components/FavoriteTeamModal/_useFavoriteTeamModalSelection";
import { FavoriteItemTypeEnum } from "@graphql/__generated__/types";
import Auth from "@hooks/useAuth";
import Message, { PredefinedMessages } from "@hooks/useMessage";
import { isOfflineError } from "@libs/apolloClient";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import { useFavoriteTeam_UpdateFavoritesMutation as useUpdateFavoritesMutation } from "./__generated__/mutations";
import type { FavoriteTeam_UsersTeamFragment as UsersTeamFragment } from "./__generated__/fragments";

type OnSavedCallback = (
  items: FavoriteItem[],
  updatedFavoriteTeams: UsersTeamFragment[]
) => void;

export const useFavoriteUpdate = (onSaved?: OnSavedCallback) => {
  const { fanstaUser, loadFanstaUser } = Auth.useContainer();
  const { addMessage } = Message.useContainer();
  const [updateFavorites] = useUpdateFavoritesMutation();

  // favorites フィールドから FavoriteItem[] を復元（チーム・スポーツ混在）
  const initialItems = useMemo(
    (): FavoriteItem[] =>
      fanstaUser?.currentUser.user?.favorites.flatMap((f): FavoriteItem[] => {
        if (f.__typename === "UsersUserFavoriteTeamType" && "team" in f) {
          return [{ type: FavoriteItemTypeEnum.Team, id: f.team.id }];
        }
        if (
          f.__typename === "UsersUserFavoriteSportGenreType" &&
          "sport" in f
        ) {
          return [{ type: FavoriteItemTypeEnum.SportGenre, id: f.sport.id }];
        }
        return [];
      }) ?? [],
    [fanstaUser]
  );

  const appliedHandler = async (items: FavoriteItem[]): Promise<boolean> => {
    try {
      const result = await updateFavorites({
        variables: {
          items: items.map((item) => ({ type: item.type, id: item.id })),
        },
      });
      if (result.errors) {
        return false;
      }
      const teamIds = items
        .filter((item) => item.type === FavoriteItemTypeEnum.Team)
        .map((item) => item.id);
      GTMEvent(GTMEvents.favoriteTeamSaved(teamIds));
      const updatedFavoriteTeams = (result.data?.updateFavorites
        ?.favoriteTeams ?? []) as UsersTeamFragment[];
      onSaved?.(items, updatedFavoriteTeams);
      loadFanstaUser && loadFanstaUser();
      return true;
    } catch (err) {
      isOfflineError(err)
        ? addMessage(PredefinedMessages.offlineError)
        : addMessage(PredefinedMessages.commonError);
      return false;
    }
  };

  return { initialItems, appliedHandler };
};
